import React, { useMemo } from 'react';
import type { UseFormReturnType } from '@mantine/form';

import type { BRANDS } from '~/domains/auth/consts';

import { VerifyType } from '../../hooks/useProvePrefill';

import { EmailForm } from './components/EmailForm';
import { PasswordForm } from './components/PasswordForm';
import { HandleForm } from './components/HandleForm';
import { KYCStartForm } from './components/KYCStartForm';
import { ConfirmInfoCard } from './components/ConfirmInfoCard';
import { KYCDetailsForm } from './components/KYCDetailsForm';
import type { SignUpFormValues, KYCFormValues } from './types';
import { KYCDetailsMode } from './types';

export const enum StepId {
  EMAIL = 'email',
  PASSWORD = 'password',
  HANDLE = 'handle',
  KYC_START = 'kyc-start',
  CONFIRM_INFO = 'confirm-info',
  KYC_DETAILS = 'kyc-details',
}

export type StepDefinition = {
  id: StepId;
  label: string;
  render: () => React.ReactNode;
};

type UseSignUpStepsParams = {
  steps: string[];
  user: unknown;
  brand: BRANDS;
  formSignUp: UseFormReturnType<SignUpFormValues>;
  formKYC: UseFormReturnType<KYCFormValues>;
  isProveKycEnabled: boolean;
  verifyType: VerifyType | null;
  isProveInitializing: boolean;
  isRestrictedDomain: boolean;
  handleSetEmail: () => void;
  handleSetPassword: () => void;
  handleSetHandle: () => void;
  isHandleLoading: boolean;
  handleKeyDownHandle: (event: KeyboardEvent) => void;
  handleSelectVerifyType: (type: VerifyType) => void;
  handleProveSendCode: () => void;
  handleSetAddress: () => void;
  handleEditInfo: () => void;
  isEditingFromConfirm: boolean;
  isKycLoading: boolean;
  preAuthStepCount: number;
};

export function useSignUpSteps(params: UseSignUpStepsParams): StepDefinition[] {
  const {
    steps,
    user,
    brand,
    formSignUp,
    formKYC,
    isProveKycEnabled,
    verifyType,
    isProveInitializing,
    isRestrictedDomain,
    handleSetEmail,
    handleSetPassword,
    handleSetHandle,
    isHandleLoading,
    handleKeyDownHandle,
    handleSelectVerifyType,
    handleProveSendCode,
    handleSetAddress,
    handleEditInfo,
    isEditingFromConfirm,
    isKycLoading,
    preAuthStepCount,
  } = params;

  function buildPreAuthSteps(): StepDefinition[] {
    return [
      {
        id: StepId.EMAIL,
        label: steps[0],
        render: () =>
          !user ? <EmailForm form={formSignUp} onClick={handleSetEmail} brand={brand} /> : null,
      },
      {
        id: StepId.PASSWORD,
        label: steps[1],
        render: () =>
          !user ? <PasswordForm form={formSignUp} onClick={handleSetPassword} /> : null,
      },
      {
        id: StepId.HANDLE,
        label: steps[2],
        render: () =>
          user ? (
            <HandleForm
              form={formSignUp}
              onClick={handleSetHandle}
              isLoading={isHandleLoading}
              brand={brand}
              handleKeyDownHandle={handleKeyDownHandle}
            />
          ) : null,
      },
    ];
  }

  function buildKycSteps(): StepDefinition[] {
    const kycBase = preAuthStepCount;

    if (!isProveKycEnabled) {
      return [
        {
          id: StepId.KYC_DETAILS,
          label: steps[kycBase],
          render: () =>
            user ? (
              <KYCDetailsForm
                form={formKYC}
                onClick={handleSetAddress}
                mode={KYCDetailsMode.VERIFY}
                isRestrictedDomain={isRestrictedDomain}
                isLoading={isKycLoading}
              />
            ) : null,
        },
      ];
    }

    const kycStartStep: StepDefinition = {
      id: StepId.KYC_START,
      label: steps[kycBase],
      render: () =>
        user ? (
          <KYCStartForm
            form={formKYC}
            onContinue={handleProveSendCode}
            onEnterManually={() => handleSelectVerifyType(VerifyType.Manual)}
            isLoading={isProveInitializing}
            isRestrictedDomain={isRestrictedDomain}
          />
        ) : null,
    };

    if (verifyType === VerifyType.Manual) {
      return [
        kycStartStep,
        {
          id: StepId.KYC_DETAILS,
          label: steps[kycBase + 1],
          render: () =>
            user ? (
              <KYCDetailsForm
                form={formKYC}
                onClick={handleSetAddress}
                mode={isEditingFromConfirm ? KYCDetailsMode.EDIT : KYCDetailsMode.MANUAL}
                isRestrictedDomain={isRestrictedDomain}
                isLoading={isKycLoading}
              />
            ) : null,
        },
      ];
    }

    return [
      kycStartStep,
      {
        id: StepId.CONFIRM_INFO,
        label: steps[kycBase + 1],
        render: () =>
          user ? (
            <ConfirmInfoCard form={formKYC} onConfirm={handleSetAddress} onEdit={handleEditInfo} />
          ) : null,
      },
    ];
  }

  return useMemo(
    () => [...buildPreAuthSteps(), ...buildKycSteps()],
    [
      user,
      steps,
      brand,
      formSignUp.values,
      formKYC.values,
      isProveKycEnabled,
      verifyType,
      isEditingFromConfirm,
      isProveInitializing,
      isRestrictedDomain,
      isKycLoading,
      isHandleLoading,
    ]
  );
}
